import React from "react";
import PropTypes from "prop-types";

import { Ring, Donut, Gauge, TargetBar, BigNumber } from "../CustomWidget";
import { formatFull } from "./format";

/**
 * The single-value graph styles from "Build Your Own" (Number / Target bar /
 * Progress ring / Donut / Gauge) — Kelvin's ask: adapt that picker's options
 * to Goal Progress and make them actually work, not just sit in the list.
 * Reuses the SAME shape components Build Your Own's own cards render with
 * (exported from CustomWidget/index.jsx), so a Number card here and a Number
 * card there are pixel-identical, not a lookalike redrawn from scratch.
 *
 * The running-total-over-time styles (Area / Line / Bar / Sparkline) stay in
 * GoalTrendChart — a single point-in-time shape has nothing to plot a trend
 * with; this component only ever renders when `style` is one of the five
 * single-value kinds.
 */
const GoalShape = ({ style, amount, goal, kind, color, reached }) => {
  const hasGoal = goal > 0;
  const pct = hasGoal ? amount / goal : 0;
  const center = hasGoal ? `${Math.round(pct * 100)}%` : formatFull(amount, kind);
  const sub = hasGoal ? `of ${formatFull(goal, kind)}` : null;
  const shapeColor = reached ? "var(--gprog-good)" : color;

  if (style === "target") {
    return (
      <div className="gprog-shape">
        <TargetBar pct={pct} color={shapeColor} value={amount} target={goal} kind={kind} />
      </div>
    );
  }
  if (style === "ring") {
    return (
      <div className="gprog-shape">
        <Ring pct={pct} color={shapeColor} center={center} sub={sub} />
      </div>
    );
  }
  if (style === "donut") {
    return (
      <div className="gprog-shape">
        <Donut pct={pct} color={shapeColor} center={center} sub={sub} />
      </div>
    );
  }
  if (style === "gauge") {
    return (
      <div className="gprog-shape">
        <Gauge pct={pct} color={shapeColor} center={center} sub={sub} />
      </div>
    );
  }
  // "number" — the hero value up top already carries this; nothing else to draw.
  return (
    <div className="gprog-shape">
      <BigNumber value={amount} kind={kind} label={hasGoal ? `${Math.round(pct * 100)}% of goal` : null} />
    </div>
  );
};

GoalShape.propTypes = {
  style: PropTypes.oneOf(["number", "target", "ring", "donut", "gauge"]).isRequired,
  amount: PropTypes.number,
  goal: PropTypes.number,
  kind: PropTypes.oneOf(["$", "%", "#"]),
  color: PropTypes.string,
  reached: PropTypes.bool,
};
GoalShape.defaultProps = {
  amount: 0,
  goal: 0,
  kind: "#",
  color: "var(--gprog-brand)",
  reached: false,
};

export default GoalShape;
